import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, setSEO } from "@/lib/api";
import PlaceholderArt from "@/components/PlaceholderArt";
import ProductCard from "@/components/ProductCard";
import ProductGallery from "@/components/ProductGallery";
import SizeAvailability from "@/components/SizeAvailability";
import SizingNote from "@/components/SizingNote";
import { formatApiError } from "@/context/AuthContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    setNotFound(false);
    setP(null);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setP(r.data);
        setSEO({
          title: `${r.data.name} | FEMBOT NEX`,
          description: r.data.short_desc,
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  async function onNotify(e) {
    e.preventDefault();
    if (!email) return;
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/notify", {
        email,
        product_slug: slug,
        page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      setState({ status: "success", message: data.message });
      setEmail("");
    } catch (err) {
      setState({
        status: "error",
        message: formatApiError(err.response?.data?.detail) || "TRANSMISSION FAILED.",
      });
    }
  }

  if (notFound) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-32 text-center">
        <div className="section-label">// 404</div>
        <h1 className="mt-3 font-display text-5xl md:text-7xl">File Not Found.</h1>
        <p className="mt-4 text-[#a3a3a3]">The garment record you requested does not exist.</p>
        <Link to="/shop" className="mt-8 inline-flex btn-primary">
          Return to Catalog <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 font-mono text-xs tracking-[0.2em] uppercase text-[#6b6b6b]">
        // loading garment file...
      </div>
    );
  }

  const locked = p.tier === "contraband";

  return (
    <div data-testid="product-detail-page" className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-16">
      <Link
        to="/shop"
        data-testid="product-back-link"
        className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#a3a3a3] hover:text-white"
      >
        <ArrowLeft size={13} strokeWidth={1.5} /> Back to catalog
      </Link>

      <div className="mt-8 grid md:grid-cols-12 gap-px bg-[#262626] border border-[#262626]">
        <div className="md:col-span-7 bg-[#050505] p-4 md:p-6">
          {Array.isArray(p.images) && p.images.length > 0 ? (
            <ProductGallery images={p.images} alt={p.name} />
          ) : (
            <PlaceholderArt label={p.placeholder_label} sku={p.sku} locked={locked} />
          )}
        </div>
        <div className="md:col-span-5 bg-[#050505] p-6 md:p-10 flex flex-col gap-6">
          <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.2em] uppercase">
            <span className={locked ? "badge badge-locked" : "badge"}>{p.status_badge}</span>
            <span className="text-[#6b6b6b]">SKU // {p.sku}</span>
          </div>
          <h1
            data-testid="product-title"
            className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight"
          >
            {p.name}
          </h1>
          <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#a3a3a3]">
            // Category: {p.category}
            {p.subcategory && <span className="ml-2 text-[#c084fc]">· {p.subcategory}</span>}
          </div>
          <div className="font-mono text-xl tracking-[0.1em] text-white">
            {p.price ? `$${p.price} USD` : "PRICE AT DROP"}
          </div>
          <p className="text-[#a3a3a3] leading-relaxed">{p.long_desc}</p>

          <div className="border-t border-[#1a1a1a] pt-6 space-y-4">
            <SizingNote variant="compact" />
            {[
              ["Fit Notes", p.fit_notes],
              ["Fabric Notes", p.fabric_notes],
              ["Style Notes", p.style_notes],
              ["Shipping / Preorder", p.shipping_notes],
            ]
              .filter(([, v]) => Boolean(v))
              .map(([k, v]) => (
                <div key={k}>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">// {k}</div>
                  <div className="mt-1 text-sm text-white/90 leading-relaxed">{v}</div>
                </div>
              ))}
          </div>

          <SizeAvailability sizes={p.sizes} sizeType={p.size_type} />

          <div className="border-t border-[#1a1a1a] pt-6">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493] mb-3">
              // {locked ? "Contraband Access" : "Join Drop List"}
            </div>
            <form onSubmit={onNotify} data-testid="product-notify-form" className="flex flex-col sm:flex-row gap-0">
              <input
                data-testid="product-notify-email"
                type="email"
                name="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                enterKeyHint="send"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Terminal address (email)"
                className="field sm:flex-1"
              />
              <button data-testid="product-notify-submit" className="btn-primary justify-center sm:w-auto">
                {locked ? "Authorize" : "Notify Me"} <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            </form>
            {state.message && (
              <p
                data-testid="product-notify-message"
                className={`mt-3 font-mono text-[11px] tracking-[0.18em] uppercase ${
                  state.status === "success" ? "text-[#00ff41]" : "text-[#ff3b30]"
                }`}
              >
                // {state.message}
              </p>
            )}
            <Link
              to="/sizing"
              data-testid="product-sizing-link"
              className="mt-4 inline-block font-mono text-[11px] tracking-[0.2em] uppercase text-[#a3a3a3] hover:text-white"
            >
              View size guide →
            </Link>
          </div>
        </div>
      </div>

      {p.related && p.related.length > 0 && (
        <section className="mt-24 md:mt-32">
          <div className="section-label">// Related Files</div>
          <h3 className="mt-3 font-display text-2xl md:text-4xl tracking-tight">You may also breach.</h3>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
            {p.related.map((rp) => (
              <ProductCard key={rp.slug} product={rp} locked={rp.tier === "contraband"} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
